<template>
    <div>
        <div class="container">
            <v-progress-linear v-if="loading" indeterminate></v-progress-linear>
            <div v-for="(table, index) in formattedData" :key="index" class="card">
                <div class="card-header">
                    <h2>{{ table.tableName }}</h2>
                    <span><strong>Search Parameters:</strong> {{ table.searchParameters.join(' || ') }}</span>
                </div>
                <div class="no-result-container" v-if="table.missingDataMessage">
                    <h4>{{table.missingDataMessage}}</h4>
                    <template v-if="table.documentsFound && table.documentsFound.length">
                        <h6>Supporting Documents</h6>
                        <div class="screenshot-docs-container">
                            <div v-for="docValue in table.documentsFound" :key="docValue.name" class="screenshot-doc">
                                <h5>{{ docValue.name }}</h5>
                                <scraped-value-display :value="docValue.file_path" />
                            </div>
                        </div>
                    </template>
                </div>

                <div class="card-table" v-if="table.data && table.data.length&&table.availableDataMessage">
                    <h4>{{table.availableDataMessage}}</h4>
                    <customTable :headers="table.headers" :items="table.data">
                      <template v-for="header in table.headers" v-slot:[`item.${header.value}`]="{ value }">
    <scraped-value-display :key="header.value" :value="String(value)" />
  </template>
                        <template #item.action="{ item }">
                            <div class="action-column">
                                <v-btn
                                    color="primary"
                                    class="navigation-btn"
                                    v-if="item.site_url"
                                    @click="handleAction({file_path: item.site_url},'open-site')"
                                    >Link to case details</v-btn
                                >
                                <v-btn color="primary" class="navigation-btn" @click="viewCaseDetails(item)"
                                    >View case details</v-btn
                                >
                            </div>
                        </template>
                    </customTable>
                </div>
            </div>
        </div>
        <caseDetailsModal :dialog.sync="dialog" :data="selectedCase" @close="handleCloseModal" />
        <filePreviewModal :dialog="documentsDialog" :data="selectedDocument" @close="documentsDialog = false;selectedDocument = null;" />
    </div>
</template>

<script>
import { GETSCRAPPEDRUNSHEETDATA } from '@/store/actions.type'
import caseDetailsModal from './courtCaseDetailsModal.vue'
import customTable from './commonComponents/customTable.vue'
import { SITE_TYPE } from '@/constants'
import { formatKeyToLabel } from '../utils'
import { COURT_CASE_TABLE_COLUMNS_ORDER, COURT_SITE_MAPPING } from '../constants'
import filePreviewModal from '@/components/filePreviewModal.vue'
import scrapedValueDisplay from '@/components/commonComponents/scrappedValueDisplay.vue'

export default {
  name: 'RunSheetCourtTab',
  components: {
    customTable,
    caseDetailsModal,
    filePreviewModal,
    scrapedValueDisplay
  },
  props: {
    order: null,
    fetchOnActive: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      scrappedData: [],
      formattedData: [],
      selectedCase: null,
      loading: false,
      dialog: false,
      documentsDialog: false,
      selectedDocument: null,
      selectedData: null
    }
  },
  mounted () {
    this.getData()
  },
  methods: {
    getData () {
      this.loading = true

      this.$store.dispatch(GETSCRAPPEDRUNSHEETDATA, {
        orderId: this.order.id,
        siteType: SITE_TYPE.COURT
      }).then(response => {
        this.scrappedData = response.results
        this.formatAndSetData()
      }).finally(() => {
        this.loading = false
      })
    },
    formatAndSetData () {
      if (this.scrappedData && this.scrappedData.length) {
        const data = this.scrappedData.find(item => item.data_type === 'json')
        if (data && data.json_data) {
          const rawData = data.json_data
          const formatted = []

          for (const [courtType, courtDetails] of Object.entries(rawData)) {
            const dataToSet = {
              tableName: COURT_SITE_MAPPING[courtType] || courtType,
              sourceTab: courtType,
              searchParameters: [],
              headers: [],
              documentsFound: [],
              data: [],
              missingDataMessage: '',
              availableDataMessage: ''
            }

            let missingPersonsData = []
            let availablePersonsData = []
            for (const [personName, personData] of Object.entries(courtDetails)) {
              if (personName) {
                dataToSet.searchParameters.push(formatKeyToLabel(personName))
              }
              if (personData) {
                let documentsFound = personData.documents_found || []
                let casesData = personData.cases || []

                documentsFound = documentsFound.map(doc => ({
                  file_path: doc.url,
                  name: formatKeyToLabel(doc.name || 'document')
                }))

                if (!casesData.length) {
                  missingPersonsData.push(personName)
                } else {
                  if (!dataToSet.headers.length) {
                    const headers = Object.keys(casesData[0]).filter(
                      (key) => {
                        const value = casesData[0][key]
                        const keysToExclude = ['site_url', 'location']
                        return !keysToExclude.includes(key) && (typeof value === 'string' || typeof value === 'number')
                      }
                    ).sort((a, b) => {
                      return (COURT_CASE_TABLE_COLUMNS_ORDER[a] || 1000) - (COURT_CASE_TABLE_COLUMNS_ORDER[b] || 1000)
                    }).map((key) => {
                      return {
                        text: formatKeyToLabel(key),
                        value: key,
                        sortable: true
                      }
                    })
                    dataToSet.headers = headers
                  }
                  const headers = dataToSet.headers

                  casesData = casesData.map((caseItem) => {
                    const courtSite = COURT_SITE_MAPPING[courtType] || courtType
                    const row = { courtSite }
                    for (const key of headers) {
                      row[key] = caseItem[key]
                    }

                    // Keep other nested fields as-is
                    for (const [key, value] of Object.entries(caseItem)) {
                      if (!headers.includes(key)) {
                        row[key] = value
                      }
                    }

                    return row
                  })
                  availablePersonsData.push(personName)
                }

                dataToSet.documentsFound.push(...documentsFound)
                dataToSet.data.push(...casesData)
              } else {
                missingPersonsData.push(personName)
              }
            }

            if (missingPersonsData.length) {
              missingPersonsData = [...new Set(missingPersonsData)]
              const lastPerson = missingPersonsData.pop()
              const personsList = missingPersonsData.length ? `${missingPersonsData.join(', ')} and ${lastPerson}` : lastPerson
              dataToSet.missingDataMessage = `No data available for ${personsList}.`
            }

            if (availablePersonsData.length) {
              availablePersonsData = [...new Set(availablePersonsData)]
              const lastPerson = availablePersonsData.pop()
              const personsList = availablePersonsData.length ? `${availablePersonsData.join(', ')} and ${lastPerson}` : lastPerson

              dataToSet.availableDataMessage = `Cases Data available for ${personsList}.`
            }

            const { headers, searchParameters } = dataToSet

            formatted.push({
              ...dataToSet,
              headers: [{ text: 'Court Website', value: 'courtSite', sortable: true }, ...headers, { text: 'Action', value: 'action' }],
              searchParameters: searchParameters.length ? [...new Set(searchParameters)] : []
            })
          }

          if (formatted.length) {
            this.formattedData = formatted
          } else {
            this.formattedData = []
          }
        }
      }
    },
    viewCaseDetails (item) {
      this.selectedCase = item
      this.dialog = true
    },
    handleCloseModal () {
      this.dialog = false
      this.selectedCase = null
    },
    handleAction (item, mode = 'view') {
      if (mode === 'view') {
        this.selectedDocument = item
        this.documentsDialog = true
      } else {
        const { file_path: fileLink } = item
        window.open(fileLink, '_blank')
      }
    }
  },
  watch: {
    fetchOnActive (newVal) {
      if (newVal) {
        this.getData()
      }
    }
  }
}
</script>
<style scoped>
.container {
  width: 100%;
  height: 100%;
  margin: 0 !important;
  padding: 12px 0 !important;
  max-width: 100% !important;
}

.no-result {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60px;
  height: fit-content;
  background-color: white;
  margin-top: 12px;
  padding: 12px 0 !important;
  width: 100%;
}

.no-result-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-size: 20px;
  width: 100%;
  margin-bottom: 24px;
}

h4 {
  margin-bottom:12px;
  font-size: 20px;
}

h6 {
  margin-bottom:8px;
  font-size: 16px;
}

.card {
  background: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  padding: 12px;
  margin-bottom: 12px;
}

.card-header{
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 8px;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
}

.card-table {
  margin-top: 12px;
  overflow-x: auto;
}

.navigation-btn {
  width: fit-content;
  height: 24px !important;
  border-radius: 4px;
  padding: 4px;
  font-size: 10px;
}

.action-column {
  display: flex;
  gap:12px;
}

.screenshot-doc {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 2px solid #eee;
  padding:8px;
  border-radius: 8px;
  width: 100%;
}

.screenshot-doc h5 {
  font-size: 14px;
}

h3,h4,h5 {
font-weight: 500;
}

.screenshot-docs-container{
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2,1fr);
  gap:12px;
}
</style>
